import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HelpCircle,
  Mail,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Lock,
  BarChart3,
  Download,
  RefreshCw,
  Tags
} from 'lucide-react';
import LegalLayout from '../LegalLayout';

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  const accentColor = '#10b981';

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)'
      }}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left transition-colors"
        style={{ color: 'var(--text-primary)' }}
      >
        <span className="font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: accentColor }} />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
        )}
      </button>
      {isOpen && (
        <div
          className="px-6 pb-4"
          style={{ color: 'var(--text-secondary)' }}
        >
          {answer}
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const accentColor = '#10b981';
  const [openFAQ, setOpenFAQ] = useState(0);

  const faqs = [
    {
      icon: <DollarSign className="w-5 h-5" />,
      question: "How do I log an expense?",
      answer: (
        <div className="space-y-2">
          <p>Logging an expense in TapLedger is quick and easy:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open TapLedger and you'll see the expense entry screen</li>
            <li>Enter the amount using the number pad</li>
            <li>Select a <strong>category</strong> (e.g., Food, Transport, Shopping)</li>
            <li>Choose a <strong>payment type</strong> (Cash, UPI, Bank, or Card)</li>
            <li>Optionally add a <strong>note</strong> for more context</li>
            <li>Tap the <strong>save button</strong> to log the expense</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Tip: Your most recently used payment type is remembered for faster entry next time.
          </p>
        </div>
      )
    },
    {
      icon: <Tags className="w-5 h-5" />,
      question: "Can I create custom categories?",
      answer: (
        <div className="space-y-2">
          <p>Custom categories are available with TapLedger Pro:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>The free version includes a set of default categories (Food, Transport, Shopping, etc.)</li>
            <li>With Pro, you can create unlimited custom categories with your own names and emojis</li>
            <li>Custom categories appear alongside the defaults for easy selection</li>
          </ul>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            To upgrade, tap the Pro badge in the app to view available features.
          </p>
        </div>
      )
    },
    {
      icon: <BarChart3 className="w-5 h-5" />,
      question: "How do I view my spending analytics?",
      answer: (
        <div className="space-y-2">
          <p>TapLedger provides spending insights through the Summary tab:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Daily view:</strong> See your total spending and breakdown for any day (free)</li>
            <li><strong>Weekly view:</strong> View a 7-day spending trend with daily totals (Pro)</li>
            <li><strong>Monthly view:</strong> See your full month spending with daily breakdown (Pro)</li>
            <li><strong>Category breakdown:</strong> Understand where your money goes by category</li>
          </ul>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            The daily summary is available in the free version. Weekly and monthly analytics require Pro.
          </p>
        </div>
      )
    },
    {
      icon: <Download className="w-5 h-5" />,
      question: "How do I export my expense data?",
      answer: (
        <div className="space-y-2">
          <p>CSV export is available with TapLedger Pro:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open TapLedger</li>
            <li>Go to the <strong>Settings</strong> or export section</li>
            <li>Tap <strong>Export as CSV</strong></li>
            <li>Choose how to share the file (AirDrop, email, Files app, etc.)</li>
          </ol>
          <p className="mt-3">The exported CSV includes: Date, Amount, Category, Emoji, Payment Type, and Note for each transaction.</p>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            File format: TapLedger_Export_YYYY-MM-DD.csv
          </p>
        </div>
      )
    },
    {
      icon: <Lock className="w-5 h-5" />,
      question: "How do I enable App Lock?",
      answer: (
        <div className="space-y-2">
          <p>App Lock is a Pro feature that secures your expense data:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Upgrade to TapLedger Pro</li>
            <li>Go to <strong>Settings</strong></li>
            <li>Enable <strong>App Lock</strong></li>
            <li>Set a <strong>4-digit backup PIN</strong></li>
            <li>Face ID, Touch ID, or Optic ID will be used as the primary unlock method</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            The backup PIN is used when biometric authentication is unavailable.
          </p>
        </div>
      )
    },
    {
      icon: <RefreshCw className="w-5 h-5" />,
      question: "How do I restore my Pro purchase?",
      answer: (
        <div className="space-y-2">
          <p>If you've previously purchased TapLedger Pro:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open TapLedger</li>
            <li>Navigate to the <strong>Pro upgrade</strong> screen</li>
            <li>Tap <strong>Restore Purchases</strong></li>
            <li>Sign in with the same Apple ID used for the original purchase</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            If restoration fails, ensure you're signed into the correct Apple ID and have an active internet connection.
          </p>
        </div>
      )
    }
  ];

  return (
    <LegalLayout app="TapLedger" docType="Support" icon={HelpCircle}>
        {/* Contact Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <a
            href="mailto:support@feraltechlabs.co.in"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <Mail className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Email Support
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  support@feraltechlabs.co.in
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              We typically respond within 24-48 hours
            </p>
          </a>

          <a
            href="mailto:support@feraltechlabs.co.in?subject=TapLedger%20Bug%20Report"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <MessageSquare className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Report a Bug
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Help us improve TapLedger
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Include your iOS version and steps to reproduce
            </p>
          </a>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>

        {/* App Info */}
        <div
          className="rounded-xl p-6"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            App Information
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>App Name:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>TapLedger</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Developer:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Feral Techlab</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Platform:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>iOS</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Category:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Finance</span>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link
            to="/apps/tapledger/privacy"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Privacy Policy
          </Link>
          <span style={{ color: 'var(--text-tertiary)' }}>•</span>
          <Link
            to="/apps/tapledger/terms"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Terms & Conditions
          </Link>
        </div>
    </LegalLayout>
  );
};

export default Support;
